import React, { useState } from 'react';
import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Badge,
  useMediaQuery,
  useTheme,
  Divider,
  ListItemIcon,
  ListItemText,
  Switch
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as UsersIcon,
  Groups as GroupsIcon,
  Receipt as TicketsIcon,
  AccountBalance as AccountingIcon,
  VolunteerActivism as FundraiseIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Person as PersonIcon,
  ExitToApp as ExitToAppIcon,
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import LogoDisplay from '../common/LogoDisplay';

const AdminAppBar = ({ toggleDarkMode, darkMode }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));

  // Menu states
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileAnchorEl, setMobileAnchorEl] = useState(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const mobileMenuOpen = Boolean(mobileAnchorEl);
  const notificationsOpen = Boolean(notificationsAnchorEl);

  // Menu handlers
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleMobileMenuOpen = (event) => setMobileAnchorEl(event.currentTarget);
  const handleMobileMenuClose = () => setMobileAnchorEl(null);
  const handleNotificationsOpen = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
    navigate('/admin/notifications');
  };
  const handleNotificationsClose = () => setNotificationsAnchorEl(null);

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/admin/login');
  };

  // Admin navigation items
  const adminNavItems = [
    {
      label: 'Dashboard',
      action: () => navigate('/admin/dashboard')
    },
    {
      label: 'Users',
      action: () => navigate('/admin/users')
    },
    {
      label: 'Employees',
      action: () => navigate('/admin/employees')
    },
    {
      label: 'Groups',
      action: () => navigate('/admin/groups')
    },
    {
      label: 'Incident Tickets',
      action: () => navigate('/admin/tickets')
    },
    {
      label: 'Accounting',
      action: () => navigate('/admin/accounting')
    },
    {
      label: 'Fundraise',
      action: () => navigate('/admin/fundraisers')
    },
    {
      label: 'Events',
      action: () => navigate('/admin/events')
    }
  ];

  // Mock notification data
  const notifications = [
    { id: 1, text: 'New user registration', read: false, time: '5 min ago' },
    { id: 2, text: 'Ticket #1234 assigned to you', read: true, time: '1 hour ago' },
    { id: 3, text: 'New donation received', read: false, time: '3 hours ago' }
  ];
  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <>
      <MuiAppBar position="static" elevation={1}>
        <Toolbar sx={{ 
          minHeight: '64px',
          justifyContent: 'space-between',
          px: { xs: 1, sm: 2, md: 3 }
        }}>
          {/* Logo */}
          <LogoDisplay />

          {/* Mobile view */}
          {isMobile ? (
            <>
              <Box sx={{ flexGrow: 1 }} />

              <IconButton
                size="large"
                color="inherit"
                aria-label="notifications"
                onClick={handleNotificationsOpen}
                sx={{ mr: 1 }}
              >
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>

              <IconButton
                size="large"
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleMobileMenuOpen}
              >
                <MenuIcon />
              </IconButton>

              {/* Mobile Menu */}
              <Menu
                anchorEl={mobileAnchorEl}
                open={mobileMenuOpen}
                onClose={handleMobileMenuClose}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                sx={{ mt: 1 }}
              >
                {adminNavItems.map((item, index) => (
                  <MenuItem
                    key={index}
                    onClick={() => {
                      item.action();
                      handleMobileMenuClose();
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText>{item.label}</ListItemText>
                  </MenuItem>
                ))}

                <Divider />

                <MenuItem>
                  <ListItemIcon>
                    <Switch
                      checked={darkMode}
                      onChange={toggleDarkMode}
                      color="secondary"
                      size="small"
                    />
                  </ListItemIcon>
                  <ListItemText>Dark Mode</ListItemText>
                </MenuItem>

                {user && (
                  <>
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        navigate('/admin/profile');
                        handleMobileMenuClose();
                      }}
                    > 
                      <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>Profile</ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        navigate('/admin/settings');
                        handleMobileMenuClose();
                      }}
                    >
                      <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>Settings</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>
                      <ListItemIcon><ExitToAppIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>Logout</ListItemText>
                    </MenuItem>
                  </>
                )}
              </Menu>
            </>
          ) : (
            <>
              {/* Desktop/Tablet Navigation */}
              <Box sx={{
                display: 'flex',
                flexGrow: 1,
                justifyContent: 'center',
                gap: 1,
                '& .MuiButton-root': {
                  fontSize: { md: '0.8rem', lg: '0.9rem' },
                  px: { md: 1, lg: 2 },
                  textTransform: 'none',
                  fontWeight: 'medium',
                  minWidth: 'fit-content'
                }
              }}>
                {/* Admin navigation items */}
                {adminNavItems.map((item, index) => {
                  // Show all items on tablet as well (removed previous limit)
                  
                  return (
                    <Button
                      key={index}
                      color="inherit"
                      startIcon={item.icon}
                      onClick={item.action}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </Box>

              {/* Right-aligned icons */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  color="inherit"
                  onClick={handleNotificationsOpen}
                  size={isTablet ? 'small' : 'medium'}
                >
                  <Badge badgeContent={unreadCount} color="error">
                    <NotificationsIcon fontSize={isTablet ? 'small' : 'medium'} />
                  </Badge>
                </IconButton>

                <Switch
                  checked={darkMode}
                  onChange={toggleDarkMode}
                  color="secondary"
                  size={isTablet ? 'small' : 'medium'}
                  inputProps={{ 'aria-label': 'toggle dark mode' }}
                />

                {user && (
                  <>
                    <Button
                      color="inherit"
                      startIcon={
                        <Avatar
                          alt={user.firstName}
                          sx={{ 
                            width: isTablet ? 24 : 28, 
                            height: isTablet ? 24 : 28,
                            mr: 1 
                          }}
                        >
                          {user.firstName[0]}
                        </Avatar>
                      }
                      endIcon={!isTablet && <ArrowDropDownIcon />}
                      onClick={handleMenuOpen}
                      sx={{
                        minWidth: 'fit-content',
                        textTransform: 'none',
                        fontSize: isTablet ? '0.8rem' : '0.9rem'
                      }}
                    >
                      {isTablet ? user.firstName[0] : user.firstName}
                    </Button>

                    <Menu
                      anchorEl={anchorEl}
                      open={menuOpen}
                      onClose={handleMenuClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          navigate('/admin/profile');
                          handleMenuClose();
                        }}
                      >
                        <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Profile</ListItemText>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          navigate('/admin/settings');
                          handleMenuClose();
                        }}
                      >
                        <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Settings</ListItemText>
                      </MenuItem>
                      <Divider />
                      <MenuItem onClick={handleLogout}>
                        <ListItemIcon><ExitToAppIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Logout</ListItemText>
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Box>
            </>
          )}
        </Toolbar>
      </MuiAppBar>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationsAnchorEl}
        open={notificationsOpen}
        onClose={handleNotificationsClose}
        MenuListProps={{
          'aria-labelledby': 'notifications-button',
        }}
      >
        {notifications.map((notification) => (
          <MenuItem 
            key={notification.id}
            sx={{
              backgroundColor: notification.read ? 'inherit' : 'action.selected',
              maxWidth: 300
            }}
          >
            <ListItemText
              primary={notification.text}
              secondary={notification.time}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default AdminAppBar;